import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import type { JSX } from 'react';

import { SharedReportViewer } from '@/features/insights/SharedReportViewer.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';

/**
 * Public full-screen viewer for one shared automation result's insights report.
 *
 * Reached from the "Open full screen" control `ReportFrame` renders on the inline report at
 * `/scheduled/shared/:shareId` (see `SharedReportViewer`'s call site in
 * `scheduled_.shared.$shareId.tsx`). It already falls under the `/scheduled/shared` prefix in
 * `lib/share-path.ts`, so `boot.tsx` renders it with no session, same as the page it links back
 * to: no Teams SDK, no identity bootstrap, no `AuthGate`.
 */
export const Route = createFileRoute(
  '/scheduled_/shared/$shareId_/reports/$runId',
)({
  component: SharedReportPage,
});

function SharedReportPage(): JSX.Element {
  const { shareId, runId } = useParams({
    from: '/scheduled_/shared/$shareId_/reports/$runId',
  });

  /** An admin who turned the section off closed its links too — the parent share page refuses the
   * same way, and a deep link to one report must not walk around that switch. */
  if (!getRuntimeConfig().features.automations) {
    return (
      <div className="flex h-dvh w-full flex-col items-center justify-center bg-background px-6 text-center">
        <p className="text-sm text-muted-foreground">
          These results are unavailable or the link has been revoked.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-dvh w-full flex-col">
      <header className="flex shrink-0 items-center border-b border-border/40 bg-background px-4 py-3">
        <Link
          to="/scheduled/shared/$shareId"
          params={{ shareId }}
          className="text-sm font-medium text-primary hover:underline"
        >
          Back to results
        </Link>
      </header>
      {/* 832px is the width the report gets everywhere it is embedded — the chat bubble, the
          results feed, the public results page. The template lays itself out in a 760px column and
          paints its hero and page ground full-bleed, so this leaves the same margin of ground on
          each side; at exactly 760 the report reads as if its edges were cut off. */}
      <div className="min-h-0 flex-1 bg-muted/30">
        <div className="mx-auto h-full w-full max-w-[832px] bg-background shadow-sm">
          <SharedReportViewer
            shareId={shareId}
            runId={runId}
            offerFullScreen={false}
          />
        </div>
      </div>
    </div>
  );
}
